import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { LayoutGrid } from 'lucide-react';
import { Button } from '../components/common/Button';

interface MarketingLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
}

export const MarketingLayout = ({ title, subtitle, children }: MarketingLayoutProps) => {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <nav className="flex items-center justify-between px-6 py-4 bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-100">
        <Link to="/" className="flex items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <LayoutGrid size={20} />
          </div>
          <span className="text-xl font-bold text-slate-900">Keskedo</span>
        </Link>
        <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-slate-600">
          <Link to="/services" className="hover:text-indigo-600 transition-colors">Services</Link>
          <Link to="/features" className="hover:text-indigo-600 transition-colors">Features</Link>
          <Link to="/about" className="hover:text-indigo-600 transition-colors">About</Link>
          <Link to="/contact" className="hover:text-indigo-600 transition-colors">Contact</Link>
        </div>
        <div className="flex items-center space-x-4">
          <Link to="/auth/login">
            <Button variant="ghost">Login</Button>
          </Link>
          <Link to="/auth/register">
            <Button>Get Started</Button>
          </Link>
        </div>
      </nav>

      <header className="px-6 pt-20 pb-12 bg-slate-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="container mx-auto max-w-4xl text-center space-y-4"
        >
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
          )}
        </motion.div>
      </header>

      <main className="flex-1 px-6 py-16">
        <div className="container mx-auto max-w-5xl">{children}</div>
      </main>

      <footer className="bg-slate-50 border-t border-slate-200 py-16">
        <div className="container mx-auto px-6 space-y-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
            <div className="col-span-2 md:col-span-1 space-y-3">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
                  <LayoutGrid size={20} />
                </div>
                <span className="text-xl font-bold text-slate-900">Keskedo</span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                A unified ecosystem for identity, governance, and connection.
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900">Product</h4>
              <ul className="space-y-2 text-slate-500">
                <li><Link to="/services" className="hover:text-indigo-600">Services</Link></li>
                <li><Link to="/features" className="hover:text-indigo-600">Features</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900">Company</h4>
              <ul className="space-y-2 text-slate-500">
                <li><Link to="/about" className="hover:text-indigo-600">About Us</Link></li>
                <li><Link to="/careers" className="hover:text-indigo-600">Careers</Link></li>
                <li><Link to="/press" className="hover:text-indigo-600">Press</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900">Support</h4>
              <ul className="space-y-2 text-slate-500">
                <li><Link to="/faq" className="hover:text-indigo-600">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-indigo-600">Contact</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900">Legal</h4>
              <ul className="space-y-2 text-slate-500">
                <li><Link to="/privacy" className="hover:text-indigo-600">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-indigo-600">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-indigo-600">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">© 2026 Keskedo Ecosystem. All rights reserved.</p>
            <div className="flex gap-6 text-xs text-slate-400">
              <Link to="/privacy" className="hover:text-indigo-600">Privacy</Link>
              <Link to="/terms" className="hover:text-indigo-600">Terms</Link>
              <Link to="/cookies" className="hover:text-indigo-600">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
